import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useAssistTemplatesContext } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import { useComposeVoicesContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeVoicesContext";
import {
  getTemplateRewriteUserMessageString,
  getVoiceRewriteUserMessageString,
} from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionPostMessage";
import {
  appendManualMessages,
  postUserMessage,
} from "@mirage/service-conversation";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/hooks/useConversationRouterState";
import { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import { tagged } from "@mirage/service-logging";
import {
  isPreConfiguredTemplateID,
  type AssistTemplate,
} from "@mirage/shared/compose/assist-template";
import {
  isPreConfiguredVoiceID,
  type ComposeVoice,
} from "@mirage/shared/compose/compose-voice";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { UserMessageOptions } from "../containers/ConversationChat";

const logger = tagged("conversation-draft-config");

export interface SelectedTemplate {
  id: string | null;
  name: string;
  content?: string;
}

interface UseConversationDraftConfigProps {
  conversationId?: string;
  markdownDraft?: string;
  model?: string;
  setManuallyAddedSources: (sources: ChatSource[]) => void;
  writeAgentPayload?: string;
  setUserMessageOptions: (options: UserMessageOptions) => void;
}

interface UseConversationDraftConfigResult {
  selectedTemplate: SelectedTemplate | null | undefined;
  setSelectedTemplate: (template: SelectedTemplate) => void;
  selectedVoiceID: string | undefined;
  setSelectedVoiceID: (voiceID: string) => void;
  agentPayload: ComposeParams;
  onPostMessageWithVoice: (voiceID: string) => void;
  onPostMessageWithTemplate: (templateID: string) => void;
  onTemplateChange: (templateID: string | null, templateName: string) => void;
  manuallyAddedSourceDocumentIds?: string[];
  resetDraftConfigState: () => void;
}

export function useConversationDraftConfig({
  conversationId,
  markdownDraft,
  model,
  setManuallyAddedSources,
  writeAgentPayload,
  setUserMessageOptions,
}: UseConversationDraftConfigProps): UseConversationDraftConfigResult {
  const { voices } = useComposeVoicesContext();
  const { templates, defaultTemplates } = useAssistTemplatesContext();
  const [selectedTemplate, setSelectedTemplate] = useState<
    SelectedTemplate | null | undefined // allow null for 'Start from scratch' option
  >(undefined);
  const [selectedVoiceID, setSelectedVoiceID] = useState<string | undefined>(
    undefined,
  );

  /**
   * writeAgentPayload is the last agent payload that was used to generate a draft
   * and is returned from the getUserConversation endpoint to restore the draft state
   */
  useEffect(() => {
    if (!writeAgentPayload) {
      return;
    }

    try {
      const parsedPayload = JSON.parse(writeAgentPayload);

      // Hydrate template state if template_id exists
      if (parsedPayload.template_id && templates && defaultTemplates) {
        const allTemplates = [...templates, ...defaultTemplates];
        const template = allTemplates.find(
          (t: AssistTemplate) => t.id === parsedPayload.template_id,
        );

        if (template) {
          setSelectedTemplate({
            id: template.id,
            name: template.name,
            content: template.templateContent,
          });
        }
      }

      // Hydrate voice state if voice_id exists
      if (parsedPayload.voice_id && voices) {
        const voice = voices.find(
          (v: ComposeVoice) => v.id === parsedPayload.voice_id,
        );
        // if not found in api voices, this is a preconfigured voice
        if (voice || isPreConfiguredVoiceID(parsedPayload.voice_id)) {
          setSelectedVoiceID(parsedPayload.voice_id);
        } else {
          logger.error("Voice not found", parsedPayload.voice_id);
        }
      }
    } catch (error) {
      logger.error("Error parsing writeAgentPayload:", error);
    }
  }, [writeAgentPayload, templates, defaultTemplates, voices]);

  // pre-configured voices don't have voice samples, and only need to
  // send the default voiceId, e.g. "preset_analytical" to the API
  const getCustomVoiceSamples = useCallback(
    (voiceID?: string) => {
      if (!voiceID || isPreConfiguredVoiceID(voiceID)) {
        return [];
      }
      return (
        voices
          ?.find((v) => v.id === voiceID)
          ?.redraftedSamples?.map((s) => s.text) ?? []
      );
    },
    [voices],
  );

  // pre-configured templates don't have template content, and only need to
  // send the default templateId, e.g. "preset_analytical" to the API
  const getCustomTemplateContent = useCallback(
    (templateID?: string | null | undefined) => {
      if (!templateID || isPreConfiguredTemplateID(templateID)) {
        return "";
      }
      return templates?.find((t) => t.id === templateID)?.templateContent ?? "";
    },
    [templates],
  );

  const agentPayload = useMemo(() => {
    return new ComposeParams({
      markdownContent: markdownDraft ?? "",
      customTemplateContent: selectedTemplate?.content ?? "",
      templateId: selectedTemplate?.id ?? "",
      customVoiceSamples: getCustomVoiceSamples(selectedVoiceID),
      voiceId: selectedVoiceID,
    });
  }, [
    selectedTemplate?.content,
    selectedTemplate?.id,
    selectedVoiceID,
    markdownDraft,
    getCustomVoiceSamples,
  ]);

  const getAgentPayloadForRewrite = useCallback(
    (voiceID?: string | undefined, templateID?: string | null | undefined) => {
      let customVoiceSamples: string[] = [];
      let customTemplateContent: string = "";
      if (voiceID) {
        customVoiceSamples = getCustomVoiceSamples(voiceID);
      }
      if (templateID) {
        customTemplateContent = getCustomTemplateContent(templateID);
      }
      return JSON.stringify({
        ...agentPayload,
        voiceId: voiceID ?? "",
        templateId: templateID ?? "",
        customVoiceSamples,
        customTemplateContent,
      });
    },
    [agentPayload, getCustomVoiceSamples, getCustomTemplateContent],
  );

  const onSelectedVoice = useCallback(
    (voiceID: string) => {
      setSelectedVoiceID(voiceID);
      if (conversationId && voices) {
        try {
          const voiceRewriteMsg = getVoiceRewriteUserMessageString(
            voiceID,
            voices,
          );
          const agentPayload = getAgentPayloadForRewrite(
            voiceID,
            selectedTemplate?.id,
          );
          postUserMessage({
            conversationId,
            message: voiceRewriteMsg,
            model: model,
            agentPayload,
            agentIds: ["rewrite"],
            skipSearch: true,
            source: getDashActionSurfaceComponentLoggingSource(
              "compose_editor_pane",
            ),
          });
          setManuallyAddedSources([]);
          setSelectedVoiceID(voiceID);
        } catch (error) {
          logger.error("Error getting voice message string:", error);
        }
      }
    },
    [
      conversationId,
      voices,
      model,
      setManuallyAddedSources,
      selectedTemplate?.id,
      getAgentPayloadForRewrite,
    ],
  );

  const onSelectedTemplate = useCallback(
    (templateID: string) => {
      if (markdownDraft && defaultTemplates && conversationId) {
        const templateRewriteMsg = getTemplateRewriteUserMessageString(
          templateID,
          templates || [],
          defaultTemplates,
        );
        const agentPayload = getAgentPayloadForRewrite(
          selectedVoiceID,
          templateID,
        );
        postUserMessage({
          conversationId,
          message: templateRewriteMsg,
          model: model,
          agentPayload,
          agentIds: ["rewrite"],
          skipSearch: true,
          source: getDashActionSurfaceComponentLoggingSource(
            "compose_editor_pane",
          ),
        });
        setManuallyAddedSources([]);
        setSelectedTemplate({
          id: templateID,
          name: templateID,
          content: templateID,
        });
      }
    },
    [
      conversationId,
      model,
      templates,
      defaultTemplates,
      markdownDraft,
      setManuallyAddedSources,
      selectedVoiceID,
      getAgentPayloadForRewrite,
    ],
  );

  const onTemplateChange = useCallback(
    (templateID: string | null, templateName: string) => {
      if (templateID === null) {
        setSelectedTemplate(null);
      } else {
        const allTemplates = [...(templates || []), ...defaultTemplates];
        const template = allTemplates.find(
          (t: AssistTemplate) => t.id === templateID,
        );
        if (template) {
          setSelectedTemplate({
            id: templateID,
            name: templateName,
            content: template.templateContent,
          });
        }
      }
      if (conversationId) {
        appendManualMessages([
          {
            conversationId: conversationId,
            message: templateName,
            llmIgnore: true,
            messageType: "userMessage",
            source: getDashActionSurfaceComponentLoggingSource(
              "compose_editor_pane",
            ),
          },
        ]);
        setUserMessageOptions({ skipSearch: true, agentIds: ["write"] });
      }
    },
    [conversationId, templates, defaultTemplates, setUserMessageOptions],
  );

  const resetDraftConfigState = useCallback(() => {
    setSelectedTemplate(undefined);
    setSelectedVoiceID(undefined);
    setManuallyAddedSources([]);
  }, [setManuallyAddedSources]);

  return {
    selectedTemplate,
    setSelectedTemplate,
    selectedVoiceID,
    setSelectedVoiceID,
    agentPayload,
    onPostMessageWithVoice: onSelectedVoice,
    onPostMessageWithTemplate: onSelectedTemplate,
    onTemplateChange,
    resetDraftConfigState,
  };
}
